import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import PageNavigation from "@/components/PageNavigation";
import SEOHead from "@/components/seo/SEOHead";
import juniorBianchim from "@/assets/junior-bianchim.jpg";
import mentoria1 from "@/assets/mentoria-1.jpg";
import mentoria2 from "@/assets/mentoria-2.jpg";
import mentoria3 from "@/assets/mentoria-3.jpg";
import mentoria4 from "@/assets/mentoria-4.jpg";
import mentoria5 from "@/assets/mentoria-5.jpg";
import mentoria6 from "@/assets/mentoria-6.jpg";
import coverTecnicas from "@/assets/cover-tecnicas-fechamento.jpg";
import { 
  ArrowRight, 
  Clock, 
  User, 
  Target, 
  Lightbulb, 
  MessageSquare, 
  ThumbsUp,
  Zap,
  Trophy,
  Shield,
  AlertTriangle,
  CheckCircle,
  ChevronRight,
  BookOpen
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const TecnicasFechamento = () => {
  const navigate = useNavigate();

  const techniques = [
    {
      number: "01",
      title: "Fechamento por Alternativa",
      subtitle: "Dê duas opções, ambas com SIM",
      description: "Em vez de perguntar 'Você quer comprar?', ofereça duas alternativas que pressupõem o fechamento.",
      example: '"Você prefere a entrega para segunda ou quarta-feira?" ou "Vai querer o pacote básico ou o premium?"',
      tip: "Funciona porque elimina o 'não' como opção natural",
      icon: Target
    },
    {
      number: "02", 
      title: "Fechamento Presumido",
      subtitle: "Aja como se já estivesse vendido",
      description: "Comporte-se naturalmente como se o cliente já tivesse decidido comprar. Continue para os próximos passos.",
      example: '"Então, para o cadastro vou precisar do seu CNPJ..." ou "Vou preparar o contrato para amanhã..."',
      tip: "A confiança do vendedor transmite segurança ao comprador",
      icon: ThumbsUp
    },
    {
      number: "03",
      title: "Fechamento por Urgência",
      subtitle: "Crie senso de escassez genuíno",
      description: "Mostre ao cliente que há um benefício real em decidir agora, seja por prazo, estoque ou condição especial.",
      example: '"Essa condição é válida até sexta-feira..." ou "Temos apenas 3 unidades nessa configuração..."',
      tip: "Nunca invente urgência falsa - isso destrói credibilidade",
      icon: Clock
    },
    {
      number: "04",
      title: "Fechamento por Recapitulação",
      subtitle: "Resuma os benefícios acordados",
      description: "Liste todos os pontos que o cliente concordou durante a conversa, criando um momentum positivo.",
      example: '"Então, você precisa de X, valoriza Y, e Z resolve seu problema atual. O próximo passo seria..."',
      tip: "Use as próprias palavras do cliente na recapitulação",
      icon: CheckCircle
    },
    {
      number: "05",
      title: "Fechamento por Teste de Objeção",
      subtitle: "Descubra o que falta para fechar",
      description: "Pergunte diretamente se existe algo impedindo a decisão. Isso traz objeções ocultas à tona.",
      example: '"Se resolvermos a questão do prazo, podemos avançar?" ou "O que falta para tomarmos essa decisão hoje?"',
      tip: "Escute ativamente - muitas vezes o cliente revela a verdadeira objeção",
      icon: MessageSquare
    },
    {
      number: "06",
      title: "Fechamento por História",
      subtitle: "Use cases de sucesso como espelho",
      description: "Conte a história de um cliente similar que teve sucesso com a solução. Isso cria identificação.",
      example: '"Uma empresa do mesmo segmento que vocês estava na mesma situação há 6 meses. Hoje eles..."',
      tip: "Histórias ativam emoções - decisões são emocionais com justificativas racionais",
      icon: BookOpen
    },
    {
      number: "07",
      title: "Fechamento Silencioso",
      subtitle: "O poder de não falar",
      description: "Após fazer a proposta ou pergunta de fechamento, fique em silêncio. Quem fala primeiro, perde.",
      example: "Apresente o valor, faça a pergunta de fechamento e aguarde. O silêncio cria pressão natural para resposta.",
      tip: "Pratique tolerância ao silêncio - 10 segundos parecem uma eternidade, mas funcionam",
      icon: Zap
    }
  ];

  const commonMistakes = [
    "Falar demais após fazer a proposta",
    "Não pedir a venda diretamente",
    "Desistir após a primeira objeção",
    "Não criar urgência genuína",
    "Focar em features, não em benefícios"
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "7 Técnicas de Fechamento que Todo Vendedor Precisa Dominar",
    "description": "Aprenda as 7 técnicas de fechamento mais eficazes para aumentar sua taxa de conversão em vendas. Guia completo com exemplos práticos.",
    "author": {
      "@type": "Person",
      "name": "Junior Bianchim"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Vendas Trainer"
    },
    "datePublished": "2025-08-20",
    "dateModified": "2025-08-20"
  };

  return (
    <>
      <SEOHead 
        title="7 Técnicas de Fechamento que Todo Vendedor Precisa Dominar"
        description="Aprenda as 7 técnicas de fechamento mais eficazes para aumentar sua taxa de conversão. Guia completo com exemplos práticos e dicas de especialista."
        keywords="técnicas de fechamento, vendas, como fechar vendas, fechamento presumido, objeções de vendas, aumentar conversão"
        canonicalUrl="https://vendastrainer.com/posts/7-tecnicas-fechamento"
        ogType="article"
        articlePublishedTime="2024-12-20T00:00:00Z"
        articleAuthor="Junior Bianchim"
      />
      
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />

      <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/10">
        <Header />

        <main id="main-content">
          {/* Hero Section - Full Width */}
          <article>
            <header className="relative py-12 sm:py-20 md:py-32 overflow-hidden">
              <div 
                className="absolute inset-0 bg-cover bg-center opacity-20"
                style={{ 
                  backgroundImage: `url(${coverTecnicas})` 
                }}
                role="img"
                aria-label="Imagem de fundo representando negociação de vendas"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/80 to-background" />
              
              <div className="container mx-auto px-4 relative z-10">
                <div className="max-w-4xl mx-auto text-center">
                  <div className="flex items-center justify-center gap-4 mb-6">
                    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/50 bg-primary/10 backdrop-blur-sm">
                      <Target className="w-4 h-4 text-primary" aria-hidden="true" />
                      <span className="text-sm font-medium text-primary">Técnicas de Vendas</span>
                    </span>
                  </div>
                  
                  <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold leading-tight mb-6">
                    <span className="bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent">
                      7 Técnicas de Fechamento
                    </span>
                    <br />
                    <span className="text-foreground">que Todo Vendedor Precisa Dominar</span>
                  </h1>
                  
                  <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
                    Descubra as estratégias comprovadas por especialistas para transformar leads em clientes 
                    e aumentar drasticamente sua taxa de conversão.
                  </p>

                  <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-primary" aria-hidden="true" />
                      <span>Por <strong className="text-foreground">Junior Bianchim</strong></span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-primary" aria-hidden="true" />
                      <time dateTime="2025-08-20">20 Ago 2025</time>
                      <span>• 8 min de leitura</span>
                    </div>
                  </div>
                </div>
              </div>
            </header>

            {/* Intro Section - 2 Columns */}
            <section className="py-10 sm:py-16 bg-muted/30" aria-labelledby="intro-heading">
              <div className="container mx-auto px-4">
                <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-center">
                  <div className="flex-1 order-2 md:order-1">
                    <h2 id="intro-heading" className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">
                      Por que o Fechamento é o Momento Mais Importante da Venda?
                    </h2>
                    <p className="text-muted-foreground mb-4">
                      Você pode ter a melhor apresentação, o melhor produto e o cliente mais interessado. 
                      Mas se não souber fechar, <strong className="text-foreground">todo esse trabalho foi em vão</strong>.
                    </p>
                    <p className="text-muted-foreground mb-6">
                      O fechamento não é um momento mágico que acontece no final da conversa. É o resultado 
                      de um processo bem conduzido, onde cada técnica é aplicada no momento certo.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="flex items-center gap-3 p-4 rounded-lg bg-card border border-primary/20">
                        <Trophy className="w-8 h-8 text-primary" aria-hidden="true" />
                        <div>
                          <p className="font-bold text-2xl text-primary">63%</p>
                          <p className="text-sm text-muted-foreground">dos vendedores não pedem a venda</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-4 rounded-lg bg-card border border-accent/20">
                        <Zap className="w-8 h-8 text-accent" aria-hidden="true" />
                        <div>
                          <p className="font-bold text-2xl text-accent">80%</p>
                          <p className="text-sm text-muted-foreground">das vendas precisam de 5+ follow-ups</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 order-1 md:order-2">
                    <figure>
                      <img 
                        src={mentoria2}
                        alt="Junior Bianchim em treinamento de técnicas de fechamento com equipe comercial"
                        className="rounded-2xl shadow-2xl w-full object-cover aspect-video"
                        loading="lazy"
                      />
                      <figcaption className="sr-only">
                        Equipe comercial em sessão de treinamento
                      </figcaption>
                    </figure>
                  </div>
                </div>
              </div>
            </section>

            {/* Techniques Section - Alternating Layout */}
            <section className="py-12 sm:py-20" aria-labelledby="techniques-heading">
              <div className="container mx-auto px-4">
                <div className="text-center mb-8 sm:mb-16">
                  <h2 id="techniques-heading" className="text-2xl sm:text-4xl font-bold mb-4">
                    <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      As 7 Técnicas Essenciais
                    </span>
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto">
                    Cada técnica tem seu momento ideal de aplicação. Domine todas e saberá qual usar em cada situação.
                  </p>
                </div>

                <div className="space-y-12 sm:space-y-24">
                  {techniques.map((technique, index) => {
                    const Icon = technique.icon;
                    const isEven = index % 2 === 0;
                    
                    return (
                      <div 
                        key={technique.number}
                        className={`flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} gap-6 sm:gap-12 items-center`}
                      >
                        <div className="flex-1 w-full">
                          <div className="flex items-center gap-3 sm:gap-4 mb-4">
                            <span className="text-4xl sm:text-6xl font-bold text-primary/20">{technique.number}</span>
                            <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center flex-shrink-0">
                              <Icon className="w-5 h-5 sm:w-7 sm:h-7 text-white" aria-hidden="true" />
                            </div>
                          </div>
                          <h3 className="text-xl sm:text-2xl font-bold mb-2">{technique.title}</h3>
                          <p className="text-base sm:text-lg text-primary mb-3 sm:mb-4">{technique.subtitle}</p>
                          <p className="text-sm sm:text-base text-muted-foreground mb-4 sm:mb-6">{technique.description}</p>
                          
                          <blockquote className="border-l-4 border-accent pl-3 sm:pl-4 py-2 mb-4 sm:mb-6 bg-accent/5 rounded-r-lg">
                            <p className="italic text-sm sm:text-base text-foreground">{technique.example}</p>
                          </blockquote>
                          
                          <div className="flex items-start gap-3 p-4 rounded-lg bg-card border border-primary/20">
                            <Lightbulb className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" aria-hidden="true" />
                            <p className="text-sm text-muted-foreground">
                              <strong className="text-foreground">Dica Pro:</strong> {technique.tip}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex-1 w-full">
                          <figure>
                            <img 
                              src={[mentoria3, mentoria6, mentoria2, mentoria3, mentoria6, mentoria2, juniorBianchim][index]}
                              alt={`Junior Bianchim demonstrando a técnica ${technique.title}`}
                              className="rounded-2xl shadow-xl w-full object-cover aspect-[4/3]"
                              loading="lazy"
                            />
                          </figure>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* CTA Colorida Section */}
            <section className="py-12 sm:py-20" aria-labelledby="cta-training">
              <div className="container mx-auto px-4">
                <Card className="p-6 sm:p-12 bg-gradient-to-br from-primary via-accent to-secondary border-0 text-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-cover bg-center opacity-10" style={{ backgroundImage: `url(${mentoria2})` }} />
                  <div className="absolute inset-0 bg-gradient-to-r from-primary/90 via-accent/90 to-secondary/90" />
                  
                  <div className="relative z-10">
                    <Trophy className="w-12 h-12 sm:w-16 sm:h-16 text-white mx-auto mb-4 sm:mb-6 animate-bounce" aria-hidden="true" />
                    <h2 id="cta-training" className="text-2xl sm:text-4xl md:text-5xl font-bold mb-4 text-white">
                      Pratique Essas Técnicas Com IA
                    </h2>
                    <p className="text-base sm:text-xl text-white/90 max-w-2xl mx-auto mb-6 sm:mb-8">
                      Não basta saber - é preciso <strong>praticar</strong>. No Vendas Trainer você simula 
                      negociações reais com IA e recebe feedback instantâneo.
                    </p>
                    <Button
                      size="lg"
                      onClick={() => navigate("/auth")}
                      className="bg-white text-primary hover:bg-white/90 text-lg px-10 py-6 shadow-2xl"
                    >
                      Começar a Treinar Grátis
                      <ArrowRight className="ml-2 w-5 h-5" aria-hidden="true" />
                    </Button>
                    <p className="text-sm text-white/70 mt-4">
                      ✓ Sem cartão de crédito &nbsp; ✓ Acesso imediato &nbsp; ✓ 10 simulações grátis
                    </p>
                  </div>
                </Card>
              </div>
            </section>

            {/* Erros Comuns - 3 Columns */}
            <section className="py-12 sm:py-20 bg-muted/30" aria-labelledby="mistakes-heading">
              <div className="container mx-auto px-4">
                <div className="text-center mb-8 sm:mb-16">
                  <h2 id="mistakes-heading" className="text-2xl sm:text-4xl font-bold mb-4">
                    <span className="bg-gradient-to-r from-destructive to-destructive/70 bg-clip-text text-transparent">
                      5 Erros que Destroem Fechamentos
                    </span>
                  </h2>
                  <p className="text-muted-foreground max-w-2xl mx-auto">
                    Saber o que NÃO fazer é tão importante quanto saber o que fazer
                  </p>
                </div>

                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
                  {commonMistakes.slice(0, 3).map((mistake, index) => (
                    <Card key={index} className="p-4 sm:p-6 border-destructive/30 bg-card/50 backdrop-blur-sm hover:border-destructive/50 transition-all">
                      <div className="flex items-start gap-3 sm:gap-4">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                          <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-destructive" aria-hidden="true" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm sm:text-lg">{mistake}</p>
                          <p className="text-sm text-muted-foreground mt-2">
                            {index === 0 && "O silêncio pós-proposta é sua melhor ferramenta. Resista à tentação de preencher o vazio."}
                            {index === 1 && "Surpreendentemente, 63% dos vendedores nunca fazem a pergunta de fechamento diretamente."}
                            {index === 2 && "80% das vendas acontecem entre o 5º e 12º follow-up. Persistência inteligente é chave."}
                          </p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>

                <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mt-4 sm:mt-6">
                  {commonMistakes.slice(3).map((mistake, index) => (
                    <Card key={index + 3} className="p-4 sm:p-6 border-destructive/30 bg-card/50 backdrop-blur-sm hover:border-destructive/50 transition-all">
                      <div className="flex items-start gap-3 sm:gap-4">
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0">
                          <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-destructive" aria-hidden="true" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm sm:text-lg">{mistake}</p>
                          <p className="text-sm text-muted-foreground mt-2">
                            {index === 0 && "Urgência fabricada é facilmente percebida e destrói confiança. Use gatilhos reais."}
                            {index === 1 && "Clientes compram transformações, não especificações técnicas. Fale de resultados."}
                          </p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </section>

            {/* Seção de Profundidade - Author Bio */}
            <section className="py-12 sm:py-20" aria-labelledby="author-heading">
              <div className="container mx-auto px-4">
                <Card className="p-6 sm:p-8 md:p-12 border-primary/20 bg-card/50 backdrop-blur-sm">
                  <div className="flex flex-col md:flex-row gap-6 sm:gap-8 items-center">
                    <figure className="flex-shrink-0">
                      <img 
                        src={juniorBianchim}
                        alt="Foto de perfil de Junior Bianchim, especialista em vendas"
                        className="w-32 h-32 rounded-full object-cover border-4 border-primary"
                        loading="lazy"
                      />
                    </figure>
                    <div className="flex-1 text-center md:text-left">
                      <h2 id="author-heading" className="text-2xl font-bold mb-2">Sobre o Autor</h2>
                      <p className="text-lg text-primary mb-4">Junior Bianchim</p>
                      <p className="text-muted-foreground mb-4">
                        Vendedor com anos de experiência nas maiores empresas do Brasil, incluindo a 
                        <strong className="text-foreground"> Sênior Sistemas</strong>, maior empresa brasileira de software. 
                        Hoje é professor de Vendas, Comércio e Administração no <strong className="text-foreground">SENAC</strong> 
                        e empreendedor nas horas vagas. Criador do Vendas Trainer, plataforma de treinamento com IA.
                      </p>
                      <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                        <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm">+ 10 anos em vendas</span>
                        <span className="px-3 py-1 rounded-full bg-accent/10 text-accent text-sm">Professor SENAC</span>
                        <span className="px-3 py-1 rounded-full bg-secondary/10 text-secondary text-sm">Startupeiro</span>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </section>

            {/* Related Posts - 2 horizontal + 1 destaque */}
            <section className="py-12 sm:py-20 bg-muted/30" aria-labelledby="related-heading">
              <div className="container mx-auto px-4">
                <h2 id="related-heading" className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 text-center">
                  Continue Aprendendo
                </h2>
                
                <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-4 sm:mb-6">
                  <Card className="p-4 sm:p-6 border-primary/10 bg-card/50 backdrop-blur-sm hover:border-primary/30 transition-all cursor-pointer group">
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                      <div className="w-full sm:w-32 h-20 sm:h-24 rounded-lg bg-gradient-to-br from-accent/20 to-primary/20 flex items-center justify-center flex-shrink-0">
                        <Shield className="w-10 h-10 text-primary/50" aria-hidden="true" />
                      </div>
                      <div className="flex-1">
                        <span className="text-xs font-medium text-accent">Negociação</span>
                        <h3 className="font-bold group-hover:text-primary transition-colors">
                          Objeções de Preço: Como Transformar em Oportunidade
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Aprenda a lidar com a objeção mais comum em vendas.
                        </p>
                      </div>
                    </div>
                  </Card>
                  
                  <Card className="p-4 sm:p-6 border-primary/10 bg-card/50 backdrop-blur-sm hover:border-primary/30 transition-all cursor-pointer group">
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                      <div className="w-full sm:w-32 h-20 sm:h-24 rounded-lg bg-gradient-to-br from-secondary/20 to-accent/20 flex items-center justify-center flex-shrink-0">
                        <Trophy className="w-8 h-8 sm:w-10 sm:h-10 text-secondary/50" aria-hidden="true" />
                      </div>
                      <div className="flex-1">
                        <span className="text-xs font-medium text-secondary">Gestão</span>
                        <h3 className="font-bold group-hover:text-primary transition-colors">
                          Gamificação em Vendas: O Poder dos Pontos e Rankings
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Descubra como sistemas de gamificação motivam equipes.
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </section>

            {/* CTA Final com Urgência */}
            <section className="py-16 sm:py-24" aria-labelledby="final-cta">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto text-center">
                  <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-destructive/10 border border-destructive/30 mb-6 sm:mb-8 animate-pulse">
                    <Clock className="w-4 h-4 text-destructive" aria-hidden="true" />
                    <span className="text-xs sm:text-sm font-medium text-destructive">Oferta por tempo limitado</span>
                  </div>
                  
                  <h2 id="final-cta" className="text-2xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">
                    <span className="bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent">
                      Pare de Perder Vendas por Falta de Prática
                    </span>
                  </h2>
                  
                  <p className="text-base sm:text-xl text-muted-foreground mb-6 sm:mb-8 max-w-2xl mx-auto px-2">
                    Cada dia sem treinar é uma venda perdida. 
                    <strong className="text-foreground"> 10 simulações grátis</strong> para você testar 
                    essas técnicas com nossa IA ainda hoje.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6 sm:mb-8 px-2">
                    <Button
                      size="lg"
                      onClick={() => navigate("/auth")}
                      className="bg-gradient-to-r from-primary to-accent hover:opacity-90 text-sm sm:text-lg px-6 sm:px-10 py-4 sm:py-6 shadow-2xl animate-glow w-full sm:w-auto"
                    >
                      Quero Minhas 10 Simulações Grátis
                      <ChevronRight className="ml-2 w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
                    </Button>
                  </div>
                  
                  <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-xs sm:text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-primary" aria-hidden="true" />
                      <span>Sem cartão de crédito</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-primary" aria-hidden="true" />
                      <span>Acesso imediato</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-primary" aria-hidden="true" />
                      <span>Feedback com IA</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </article>
        </main>

        <PageNavigation />
        <Footer />
      </div>
    </>
  );
};

export default TecnicasFechamento;
